import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { formatDKK, exclMoms } from "@/lib/pricing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  quantity: 1,
  date: "",
  timeFrom: "",
  timeTo: "",
  transport: "Ingen",
  address: "",
  name: "",
  phone: "",
  email: "",
  message: "",
};

const TRANSPORT = [
  { label: "Ingen", factor: 0 },
  { label: "30 min", factor: 0.5 },
  { label: "1 time", factor: 1 },
  { label: "1,5 time", factor: 1.5 },
  { label: "2 timer", factor: 2 },
];

const field =
  "w-full rounded-lg border border-white/15 bg-[#0A0A0A] px-4 py-3 text-base text-[#F5F5F0] placeholder:text-gray-600 transition-colors duration-200 focus:border-[#D4AF37] focus:outline-none";
const lbl = "mb-1 block text-xs uppercase tracking-widest text-gray-400";

const hoursBetween = (a, b) => {
  if (!a || !b) return 0;
  const [ah, am] = a.split(":").map(Number);
  const [bh, bm] = b.split(":").map(Number);
  if ([ah, am, bh, bm].some((n) => Number.isNaN(n))) return 0;
  const diff = bh * 60 + bm - (ah * 60 + am);
  return diff > 0 ? diff / 60 : 0;
};

export const RequestModal = ({ open, onOpenChange, service, price, meta }) => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const value = meta && typeof meta.value === "number" ? meta.value : null;
  const unit = meta?.unit || null;
  const isFrom = !!meta?.isFrom;
  const hasHourly = unit === "time" && value > 0;
  const hasPerUnit = (unit === "kuvert" || unit === "gæst") && value > 0;
  const qtyLabel = hasPerUnit ? (unit === "kuvert" ? "Antal kuverter" : "Antal gæster") : "Antal";

  useEffect(() => {
    if (open) setForm(EMPTY);
  }, [open, service, price]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const qty = Math.max(1, parseInt(form.quantity, 10) || 1);
  const hours = hoursBetween(form.timeFrom, form.timeTo);
  const transportFactor = TRANSPORT.find((t) => t.label === form.transport)?.factor || 0;

  const est = useMemo(() => {
    if (hasHourly) {
      const labor = value * qty * hours;
      const transport = transportFactor * value * qty;
      const totalIncl = labor + transport;
      return { transportIncl: transport, totalIncl, totalExcl: exclMoms(totalIncl), hasTotal: totalIncl > 0 };
    }
    if (hasPerUnit) {
      const totalIncl = value * qty;
      return { transportIncl: 0, totalIncl, totalExcl: exclMoms(totalIncl), hasTotal: totalIncl > 0 };
    }
    return null;
  }, [hasHourly, hasPerUnit, value, qty, hours, transportFactor]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Udfyld venligst navn, telefon og e-mail.");
      return;
    }
    setSubmitting(true);
    try {
      const transportLabel =
        hasHourly && transportFactor > 0
          ? `${form.transport} (+${formatDKK(est.transportIncl)} kr inkl. moms)`
          : "";
      const estimate =
        est && est.hasTotal
          ? `${isFrom ? "fra " : ""}${formatDKK(est.totalIncl)} kr inkl. moms (${formatDKK(est.totalExcl, 2)} kr ekskl.)`
          : "";
      await axios.post(`${API}/request`, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        service,
        price,
        quantity: String(qty),
        date: form.date,
        time_from: form.timeFrom,
        time_to: form.timeTo,
        transport: transportLabel,
        estimate,
        address: form.address,
        details: "",
        message: form.message,
      });
      toast.success("Tak! Din anmodning er sendt. Vi vender tilbage hurtigst muligt.");
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Noget gik galt. Prøv igen.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="request-modal"
        className="max-h-[92vh] overflow-y-auto border-[#D4AF37]/20 bg-[#141414] text-[#F5F5F0] sm:max-w-lg"
      >
        <DialogHeader>
          <p className="text-xs uppercase tracking-widest text-[#D4AF37]">Anmod om ydelse</p>
          <DialogTitle className="font-heading text-3xl font-light text-[#F5F5F0]">{service}</DialogTitle>
          <DialogDescription className="sr-only">
            Vælg antal, dato, tidspunkt og eventuelt transporttillæg, og send din anmodning.
          </DialogDescription>
        </DialogHeader>

        <div
          data-testid="request-modal-price"
          className="rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-4 py-3"
        >
          <span className="text-xs uppercase tracking-widest text-gray-400">Valgt</span>
          <p className="text-2xl font-light text-[#D4AF37]">{price}</p>
        </div>

        <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>{qtyLabel}</label>
              <input
                data-testid="request-quantity"
                type="number"
                min="1"
                className={field}
                value={form.quantity}
                onChange={set("quantity")}
              />
            </div>
            <div>
              <label className={lbl}>Dato</label>
              <input data-testid="request-date" type="date" className={field} value={form.date} onChange={set("date")} />
            </div>
          </div>

          {hasHourly && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={lbl}>Fra kl.</label>
                <input data-testid="request-time-from" type="time" className={field} value={form.timeFrom} onChange={set("timeFrom")} />
              </div>
              <div>
                <label className={lbl}>Til kl.</label>
                <input data-testid="request-time-to" type="time" className={field} value={form.timeTo} onChange={set("timeTo")} />
              </div>
            </div>
          )}

          {hasHourly && (
            <div>
              <label className={lbl}>Transporttillæg</label>
              <select
                data-testid="request-transport"
                className={field}
                value={form.transport}
                onChange={set("transport")}
              >
                {TRANSPORT.map((t) => (
                  <option key={t.label} value={t.label}>
                    {t.label}
                    {t.factor > 0 ? ` (+${formatDKK(t.factor * value * qty)} kr inkl. moms)` : ""}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                30 min = halvdelen af timeprisen, 1 time = fuld timepris — pr. medarbejder.
              </p>
            </div>
          )}

          {est && est.hasTotal && (
            <div
              data-testid="request-estimate"
              className="rounded-lg border border-[#D4AF37]/30 bg-[#0F0F0F] px-4 py-3"
            >
              <span className="text-xs uppercase tracking-widest text-gray-400">Estimeret pris</span>
              <p className="text-2xl font-light text-[#D4AF37]">
                {isFrom ? "fra " : ""}{formatDKK(est.totalIncl)} kr
              </p>
              <p className="text-xs text-gray-400">{formatDKK(est.totalExcl, 2)} kr ekskl. moms</p>
            </div>
          )}

          <div>
            <label className={lbl}>
              <span className="inline-flex items-center gap-1">
                <MapPin size={13} className="text-[#D4AF37]" /> Adresse / mødested
              </span>
            </label>
            <input
              data-testid="request-address"
              className={field}
              value={form.address}
              onChange={set("address")}
              placeholder="Fx Kongensgade 1, 1234 By"
            />
          </div>

          <div>
            <label className={lbl}>Ekstra info</label>
            <textarea
              data-testid="request-message"
              rows={3}
              className={field}
              value={form.message}
              onChange={set("message")}
              placeholder="Fx hvor man skal møde, hvordan man kommer ind, parkering m.m."
            />
          </div>

          <div className="border-t border-white/10 pt-4">
            <label className={lbl}>Navn *</label>
            <input data-testid="request-name" className={field} value={form.name} onChange={set("name")} placeholder="Dit fulde navn" />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={lbl}>Telefon *</label>
              <input data-testid="request-phone" className={field} value={form.phone} onChange={set("phone")} placeholder="+45 …" />
            </div>
            <div>
              <label className={lbl}>E-mail *</label>
              <input data-testid="request-email" type="email" className={field} value={form.email} onChange={set("email")} placeholder="dig@email.dk" />
            </div>
          </div>

          <button
            type="submit"
            data-testid="submit-request-button"
            disabled={submitting}
            className="mt-2 rounded-full bg-[#D4AF37] px-8 py-3 text-sm font-medium uppercase tracking-widest text-[#0A0A0A] transition-colors duration-300 hover:bg-[#F0C74B] disabled:opacity-60"
          >
            {submitting ? "Sender…" : "Send anmodning"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
